"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronDown, MessageSquare, PhoneCall } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  question: "How can I confirm your licensing and insurance coverage?",
  answer: "We're happy to provide documentation of our insurance and any applicable licensing directly upon request before work begins."
}, {
  question: "How do you manage City of Brookhaven building permits and HOA approvals?",
  answer: "We manage the permitting lifecycle from start to finish. Our team submits architectural drawings directly to the City of Brookhaven Community Development Department, coordinates structural plan reviews, and arranges all required city inspections. If your property resides within an HOA, we prepare architectural review submissions to ensure swift architectural compliance."
}, {
  question: "What does your staged milestone payment schedule look like?",
  answer: "We operate on fixed, milestone-based billing tied to verified project progress rather than arbitrary calendar dates. Payments are distributed into transparent phases: design approval, rough-in mechanicals, finish installations, and final walkthrough sign-off. You never pay in advance for uncompleted phases."
}, {
  question: "Which areas in the North Atlanta metro do you serve?",
  answer: "Our core service territory centers on Brookhaven, Georgia and immediately adjacent neighborhoods including Buckhead, Dunwoody, Sandy Springs, Chamblee, Chastain Park, and Doraville. Focusing locally ensures our team remains available throughout the project to maintain trade accountability and quality control."
}, {
  question: "How are change orders and unforeseen site conditions handled?",
  answer: "Before breaking ground, we conduct non-destructive investigative inspections to minimize surprises behind walls and under subfloors. If an unforeseen condition occurs, our superintendent documents the issue with photos, provides a fixed-price written adjustment, and awaits your written digital sign-off before proceeding."
}];
export default function Questions() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const shouldReduceMotion = useReducedMotion();
  const toggleAccordion = (index: number) => {
    setOpenIndex(prev => prev === index ? null : index);
  };
  return <section id="frequently-asked-questions" data-nav="light" className="bg-background text-foreground py-20 md:py-24 transition-colors">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center mb-14 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="rounded-full px-3.5 py-1 text-xs font-semibold uppercase tracking-widest border-primary/40 bg-primary/10 text-primary-foreground">
              Questions & Clarity
            </Badge>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Clear answers for your{" "}
            <span className="italic font-normal text-foreground">
              Brookhaven renovation
            </span>
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Everything you should know about city permits, milestone billing, and local trade standards.
          </p>
          
          <div className="mt-6 flex items-center justify-center">
            <div className="h-[1px] w-24 bg-primary/40" />
          </div>
        </motion.div>

        {/* Accordion Stack */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="divide-y divide-border border-y border-border">
          {faqItems.map((item, i) => {
          const isOpen = openIndex === i;
          return <div key={item.question} data-index={i} className="group py-2 transition-[background-color] duration-200">
                <Button type="button" variant="ghost" onClick={() => toggleAccordion(i)} aria-expanded={isOpen} className="w-full h-auto p-0 flex items-center justify-between gap-4 py-5 text-left hover:bg-transparent whitespace-normal font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md">
                  <span className="font-sans text-lg sm:text-xl font-semibold text-foreground tracking-tight group-hover:text-primary transition-colors">
                    {item.question}
                  </span>
                  <div className={`flex items-center justify-center size-8 rounded-full border border-border/80 bg-card transition-all duration-300 shrink-0 ${isOpen ? "bg-primary text-primary-foreground border-primary rotate-180" : "text-muted-foreground group-hover:border-primary/50 group-hover:text-primary"}`}>
                    <ChevronDown className="size-4 transition-transform duration-300" />
                  </div>
                </Button>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div key="content" initial={shouldReduceMotion ? {
                opacity: 1,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: "auto",
                transition: {
                  height: {
                    duration: 0.3,
                    ease: [0.16, 1, 0.3, 1]
                  },
                  opacity: {
                    duration: 0.25,
                    delay: 0.05
                  }
                }
              }} exit={{
                opacity: 0,
                height: 0,
                transition: {
                  height: {
                    duration: 0.25,
                    ease: [0.16, 1, 0.3, 1]
                  },
                  opacity: {
                    duration: 0.15
                  }
                }
              }} className="overflow-hidden">
                      <div className="pb-6 pr-6 sm:pr-12 text-muted-foreground text-base sm:text-[17px] leading-relaxed">
                        {item.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </motion.div>

        {/* Bottom Support Prompt Card */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-14 rounded-2xl bg-card border border-border p-8 text-center shadow-sm">
          <div className="inline-flex p-3 rounded-full bg-muted text-primary mb-3">
            <MessageSquare className="size-5" />
          </div>
          <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
            Have a project-specific architectural question?
          </h3>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-lg mx-auto leading-relaxed">
            Speak directly with our Brookhaven principal builders to discuss your specific site conditions, timelines, or feasibility questions.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild variant="default" className="bg-primary text-primary-foreground font-semibold px-6 py-3 h-auto rounded-lg shadow-xs hover:bg-primary/90 transition-[transform,background-color] active:scale-95">
              <Link to="/ContactRequestAnEstimatePage">
                Schedule a consultation
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-border text-foreground hover:bg-muted font-medium px-6 py-3 h-auto rounded-lg transition-colors">
              <a href="tel:+14708479076" className="inline-flex items-center gap-2">
                <PhoneCall className="size-4 text-primary" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}